import React, { ReactElement, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useSessionStore } from '../../store/sessionStore';
import { PageBusyLoader, pageNameFromPath } from '../ui/PageBusyLoader';
import { logoutUser } from '../../api/api';
import { isAccessTokenExpired } from '../../utils/authSession';
import {
  resolveCandidateJourneyPhase,
  resolveCandidateJourneyRedirect,
  resolvePromptCount,
} from '../../utils/userProfile';

type Props = {
  children: ReactElement;
};

const PrivateRoute: React.FC<Props> = ({ children }) => {
  const [loader, setLoader] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();
  const accessToken = localStorage.getItem('accessToken');

  useEffect(() => {
    let cancelled = false;

    const checkUserData = async () => {
      if (!accessToken) {
        setLoader(false);
        return;
      }

      if (isAccessTokenExpired(accessToken)) {
        logoutUser();
        return;
      }

      try {
        const session = await useSessionStore.getState().bootstrapSession();
        const userData = session.user;
        if (cancelled || !userData) {
          return;
        }

        const promptCount = resolvePromptCount(session.prompts ?? []);
        const userRecord = userData as Record<string, unknown>;
        const phase = resolveCandidateJourneyPhase(userRecord, promptCount);
        const redirect = resolveCandidateJourneyRedirect(location.pathname, phase, location.search);

        if (redirect) {
          navigate(redirect, { replace: true });
        }
      } catch (error: unknown) {
        console.error('Error fetching data:', error);
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) {
          logoutUser();
        }
      } finally {
        if (!cancelled) {
          setLoader(false);
        }
      }
    };

    checkUserData();

    return () => {
      cancelled = true;
    };
    // Intentionally omit location.pathname: each route mounts its own PrivateRoute;
    // setup routes return early and must not fight child navigations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, navigate]);

  if (loader) {
    return <PageBusyLoader pageName={pageNameFromPath(location.pathname)} overlay />;
  }

  return accessToken ? children : <Navigate to="/" replace />;
};

export default PrivateRoute;
